import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, CornerLeftUp } from "lucide-react";
import {
  DELETED_COMMENT_TEXT,
  cachedAncestors,
  commentKey,
  commentsStore,
  useCommentsSelector,
} from "../../hooks/useCommentsStore";
import CommentBranch from "./CommentBranch";

const DEFAULT_AVATAR = `${process.env.PUBLIC_URL}/logo512.png`;

// Focused conversation: the comment `commentId` becomes the visual root
// (level 1) of a new three-level window, with up to two ancestors shown above
// it as compact, clickable context. Works from a direct link: if the focus or
// its ancestors aren't cached they're fetched via /context/. Descendants come
// from /replies/, page by page.
export default function CommentConversation({ target, commentId, actions, onBack }) {
  const byId = useCommentsSelector((s) => s.byId);
  const [loadError, setLoadError] = useState(null);
  const [attempt, setAttempt] = useState(0);

  const focus = byId[commentKey(target, commentId)];
  const ancestors = cachedAncestors(byId, target, commentId) || [];
  const oldest = ancestors[0];

  useEffect(() => {
    let canceled = false;
    setLoadError(null);
    commentsStore.loadThread(target, commentId).catch((err) => {
      if (canceled) return;
      setLoadError(
        err?.response?.status === 404
          ? "This comment isn't available."
          : "Couldn't load this conversation."
      );
    });
    return () => {
      canceled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target.type, target.id, commentId, attempt]);

  return (
    <section aria-label="Conversation" className="mb-4">
      <div className="flex items-center gap-2 mb-2">
        <button
          type="button"
          onClick={onBack}
          className="flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900 rounded"
        >
          <ArrowLeft size={16} aria-hidden="true" />
          Back
        </button>
        <h2 className="text-sm font-semibold">Conversation</h2>
      </div>

      {!focus ? (
        loadError ? (
          <div className="py-6 text-center text-sm text-gray-500" role="alert">
            <p>{loadError}</p>
            <button
              type="button"
              onClick={() => setAttempt((n) => n + 1)}
              className="mt-1 text-blue-500 hover:text-blue-600"
            >
              Retry
            </button>
          </div>
        ) : (
          <p className="py-6 text-center text-sm text-gray-400" role="status">
            Loading conversation…
          </p>
        )
      ) : (
        <>
          {oldest?.parent != null && (
            <button
              type="button"
              onClick={() => actions.onOpenConversation(oldest.parent)}
              className="flex items-center gap-1 mb-1 text-xs font-medium text-gray-500 hover:text-gray-800 rounded"
            >
              <CornerLeftUp size={12} aria-hidden="true" />
              View earlier replies
            </button>
          )}

          {ancestors.length > 0 && (
            <ol aria-label="Earlier in this conversation" className="mb-1">
              {ancestors.map((ancestor) => (
                <AncestorRow
                  key={ancestor.id}
                  comment={ancestor}
                  onOpen={() => actions.onOpenConversation(ancestor.id)}
                />
              ))}
            </ol>
          )}

          <CommentBranch commentId={focus.id} level={1} alwaysExpanded actions={actions} />
        </>
      )}
    </section>
  );
}

// Compact, dimmed parent context. The username opens the author's profile and
// the text opens that comment's conversation; they're siblings, never nested.
function AncestorRow({ comment, onOpen }) {
  return (
    <li className="flex items-start gap-2 pb-2 border-l-2 border-gray-200 pl-2 ml-2">
      {comment.is_deleted ? (
        <div className="w-4 h-4 rounded-full bg-gray-200 flex-shrink-0" aria-hidden="true" />
      ) : (
        <img
          src={comment.profile_pic_url || DEFAULT_AVATAR}
          alt=""
          className="w-4 h-4 rounded-full object-cover border flex-shrink-0"
        />
      )}
      <div className="min-w-0 flex-1 text-xs text-gray-500">
        {!comment.is_deleted && (
          <Link to={`/profiles/${comment.user}`} className="font-semibold text-gray-700 hover:underline">
            {comment.user}
          </Link>
        )}
        <button
          type="button"
          onClick={onOpen}
          className="block w-full text-left hover:text-gray-800 rounded"
          title="Open this part of the conversation"
        >
          {comment.is_deleted ? (
            <span className="italic">{DELETED_COMMENT_TEXT}</span>
          ) : (
            <span className="line-clamp-3 break-words">{comment.text}</span>
          )}
        </button>
      </div>
    </li>
  );
}
